import type {
  BeneficiaryEntityType,
  CreateShoutBeneficiaryDto,
} from '@/app/pages/admin-options/settings/molecules/creator-payment-details-fix/creator-payment-details.types';
import type { BankingRegion } from './banking-regions';
import { digitsOnly, normalizeIban } from './banking-validation';

/** Flat form model used to build a CreateShoutBeneficiaryDto. */
export interface BankingFormValues {
  region: BankingRegion | null;
  entityType: BeneficiaryEntityType;
  accountName: string;
  sortCode: string; // GB only, digits-only model
  routingNumber: string; // US only, digits-only model
  accountNumber: string; // GB/US account number
  iban: string; // EUR/SEPA, normalised uppercase
  category: 'Checking' | 'Savings';
  street: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Builds the backend DTO payload from the UI form model and selected region.
 * Mirrors the mapping used by the shout-2026 BankingTab flow and the
 * UniCreate-ligher-API create-beneficiary endpoint docs.
 */
export function buildBeneficiaryPayload(
  region: BankingRegion,
  values: BankingFormValues,
): CreateShoutBeneficiaryDto {
  const account_name = values.accountName.trim();
  const entity_type: BeneficiaryEntityType = values.entityType;

  if (region.variant === 'GB') {
    return {
      entity_type,
      bank_details: {
        account_currency: 'GBP',
        account_name,
        account_number: digitsOnly(values.accountNumber),
        account_routing_value1: digitsOnly(values.sortCode),
        bank_country_code: 'GB',
      },
    };
  }

  if (region.variant === 'US') {
    return {
      entity_type,
      bank_details: {
        account_currency: 'USD',
        account_name,
        account_number: digitsOnly(values.accountNumber),
        account_routing_value1: digitsOnly(values.routingNumber),
        bank_country_code: 'US',
        bank_account_category: values.category,
      },
      address: {
        street_address: values.street.trim(),
        city: values.city.trim(),
        state: values.state.trim(),
        postcode: values.zip.trim(),
        country_code: 'US',
      },
    };
  }

  // Default corridor: EUR / SEPA. IBAN in account_number, country from region ISO.
  const payload: CreateShoutBeneficiaryDto = {
    entity_type,
    bank_details: {
      account_currency: 'EUR',
      account_name,
      account_number: normalizeIban(values.iban),
      bank_country_code: region.iso,
    },
  };

  // Ireland LOCAL EUR requires beneficiary.address on the Airwallex account schema.
  if (region.iso === 'IE') {
    payload.address = {
      street_address: values.street.trim(),
      city: values.city.trim(),
      state: values.state.trim() || values.city.trim(),
      postcode: values.zip.trim(),
      country_code: 'IE',
    };
  }

  return payload;
}

